/**
 * Citations in a checklist ("CRC 3.110(b)", "CCP 1010.6; 1011; 1013", "LR 2.11(D)(2)", "Fee Schedule item 5") as
 * buttons that open the rules they cite in a drawer from the right, with the text of each rule or section taken from
 * its document's provisions (#160, #225).
 *
 * rule-index.json (scripts/build-content.py) says which document holds a CRC rule or a CCP or GC section, which
 * document is a court's local rules ("LR") and which is the fee schedule; src/lib/citations.js reads it and the
 * citations. A citation is a button only when the index has a document for it; the drawer then reads that document's
 * provisions (src/content/provisions/<document id>.json, written by scripts/provisions.py), each with its verbatim
 * text and its subdivisions, and marks the subdivisions cited.
 */
import { Icon } from "exo-ds/icon/vanilla";
import { h } from "./dom.js";
import { CITATION, documentIdFor, parseCitation } from "./lib/citations.js";

let index = { LR: {} };
let docById = new Map();
let hrefOf = () => null; // (court id, document) -> the document's page in that court, or null when the court doesn't show it

/** Sets what the drawer reads: the rule index, documents.json and a document's page in a court. */
export function configureRules({ ruleIndex, documents, documentHref }) {
  index = ruleIndex;
  docById = new Map(documents.map((d) => [d.id, d]));
  hrefOf = documentHref;
}

const CODE_NAME = { CRC: "Rules of Court", CCP: "Code of Civil Procedure", GC: "Government Code", LR: "Local Rules", "Fee Schedule item": "Fee schedule" };
const RANGE_MAX = 30; // sections shown for a range like "CRC 2.100-2.119"

/** The document holding `number` of `code` in court `courtId`, or null. */
export const documentFor = (code, number, courtId) => docById.get(documentIdFor(index, code, number, courtId)) || null;

/**
 * `text` with each citation whose rules the drawer can show as a button that opens it, in court `courtId`.
 * Returns text and nodes, for h().
 */
export function citeText(text, courtId) {
  if (!text) return [];
  text = String(text);
  const out = [];
  let at = 0;
  for (const m of String(text).matchAll(CITATION)) {
    const refs = parseCitation(m[0]).filter((r) => documentFor(r.code, r.number, courtId));
    if (!refs.length) continue;
    out.push(text.slice(at, m.index),
      h("button", { type: "button", class: "cite-link", "aria-haspopup": "dialog", title: `Show the text of ${m[0]}`,
        onclick: (e) => { e.preventDefault(); e.stopPropagation(); openRules(m[0], refs, courtId); } }, m[0]));
    at = m.index + m[0].length;
  }
  out.push(text.slice(at));
  return out.filter((x) => x !== "");
}

/* ------------------------------------------------------------- the rules' text */
const loaded = new Map(); // document id -> Promise of its provisions file, with byCite: cite -> [provision, ...]

/** The URL of a document's provisions file. */
const provisionsUrl = (doc) => `src/content/provisions/${encodeURIComponent(doc.id)}.json`;

/** A document's provisions (see scripts/provisions.py), fetched once. */
export function loadProvisions(doc) {
  if (!loaded.has(doc.id)) {
    const p = fetch(provisionsUrl(doc)).then((r) => { if (!r.ok) throw new Error(`${doc.id}: ${r.status}`); return r.json(); })
      .then((f) => {
        f.byCite = new Map();
        f.provisions.forEach((x) => { if (!f.byCite.has(x.cite)) f.byCite.set(x.cite, []); f.byCite.get(x.cite).push(x); });
        return f;
      });
    p.catch(() => loaded.delete(doc.id)); // so opening it again tries again
    loaded.set(doc.id, p);
  }
  return loaded.get(doc.id);
}

/**
 * The provisions a reference shows: one (each version leginfo prints), each in a range in document order, or the
 * parts of a local rule cited whole ("LR 6").
 */
export function provisionsOf(ref, f) {
  const cite = (n) => `${ref.code} ${n}`;
  if (!ref.to) {
    if (f.byCite.has(cite(ref.number))) return f.byCite.get(cite(ref.number));
    return ref.code === "LR" && !ref.number.includes(".") ? f.provisions.filter((p) => p.path[0] === `Rule ${ref.number}`) : [];
  }
  const a = f.provisions.findIndex((p) => p.cite === cite(ref.number));
  const b = f.provisions.findLastIndex((p) => p.cite === cite(ref.to));
  return a < 0 || b < a ? [] : f.provisions.slice(a, b + 1);
}

// The node of a subdivision chain ("(b)(1)") in a provision's subdivision tree, or null.
function subdivision(p, chain) {
  let node = { subdivisions: p.subdivisions || [] };
  for (const s of chain.match(/\([^)]*\)/g) || []) {
    node = (node.subdivisions || []).find((c) => c.sub === s);
    if (!node) return null;
  }
  return node;
}

/** A provision's text, with the lines of each cited subdivision (["(b)", "(b)(1)"]) marked. */
export function provisionText(p, subs = []) {
  const lines = p.text.split("\n");
  const marked = new Set();
  subs.forEach((s) => { const n = subdivision(p, s); if (n) for (let i = n.lines[0]; i < n.lines[1]; i++) marked.add(i); });
  const pre = h("pre", { class: "rule-text", tabindex: "0" });
  let chunk = [], mark = [];
  const flush = () => {
    if (chunk.length) pre.append(chunk.join("\n") + "\n");
    if (mark.length) pre.append(h("mark", null, mark.join("\n")), "\n");
    chunk = []; mark = [];
  };
  lines.forEach((l, i) => {
    if (marked.has(i)) { if (chunk.length) flush(); mark.push(l); } else { if (mark.length) flush(); chunk.push(l); }
  });
  flush();
  return pre;
}

// A provision as the drawer and the document page show it: its citation and heading, its text and its history.
export function provisionItem(p, subs = []) {
  return h("div", { class: "rule-item" },
    h("h3", { class: "rule-cite" }, p.cite, p.version ? ` (version ${p.version})` : "", p.heading ? h("span", { class: "rule-heading" }, ` ${p.heading}`) : null),
    provisionText(p, subs),
    p.history ? h("p", { class: "rule-history" }, p.history) : null);
}

/* ------------------------------------------------------------- the drawer */
let drawer = null;
let opener = null;

function closeRules() {
  if (!drawer) return;
  const d = drawer;
  drawer = null;
  d.close();
  d.remove();
  if (opener && opener.isConnected) opener.focus();
  opener = null;
}
// A link in the drawer goes to another page, which closes it.
window.addEventListener("hashchange", closeRules);

/** Opens the drawer with the rules of citation `label` (its `refs`, from parseCitation), in court `courtId`. */
export function openRules(label, refs, courtId) {
  if (drawer) { drawer.close(); drawer.remove(); }
  opener = document.activeElement;
  const body = h("div", { class: "drawer-body" });
  const d = h("dialog", { class: "drawer", "aria-labelledby": "drawer-title",
    onclick: (e) => { e.stopPropagation(); if (e.target === d) closeRules(); },
    oncancel: (e) => { e.preventDefault(); closeRules(); } },
  h("div", { class: "drawer-head" },
    h("div", null, h("div", { class: "eyebrow" }, "Cited"), h("h2", { id: "drawer-title", class: "drawer-title" }, label)),
    h("button", { type: "button", class: "icon-btn", "aria-label": "Close", title: "Close", onclick: closeRules }, Icon({ name: "cross-2" }))),
  body);
  drawer = d;
  document.body.append(d);
  d.showModal();
  let scrolled = false;
  refs.forEach((ref) => {
    const doc = documentFor(ref.code, ref.number, courtId);
    const cited = `${ref.code} ${ref.number}${ref.to ? `-${ref.to}` : ""}${ref.subs.join(", ")}`;
    const href = hrefOf(courtId, doc);
    const holder = h("div", { class: "rule-list" }, h("p", { class: "rule-note" }, "Loading..."));
    body.append(h("section", { class: "rule" },
      h("div", { class: "rule-source" }, `${CODE_NAME[ref.code]}: `, href ? h("a", { href }, doc.title) : doc.title),
      holder));
    loadProvisions(doc).then((f) => {
      const ps = provisionsOf(ref, f);
      if (!ps.length) {
        holder.replaceChildren(h("p", { class: "rule-note" }, `${cited} wasn't found in this document. `, href ? h("a", { href }, "Open the document") : null));
        return;
      }
      holder.replaceChildren(...[
        ...ps.slice(0, RANGE_MAX).map((p) => provisionItem(p, p.cite === `${ref.code} ${ref.number}` ? ref.subs : [])),
        ps.length > RANGE_MAX ? h("p", { class: "rule-note" }, `Showing the first ${RANGE_MAX} of ${ps.length}. `, href ? h("a", { href }, "Open the document") : null, " for the rest.") : null,
      ].filter(Boolean)); // replaceChildren would print a null as text
      // Scroll to the first cited subdivision, once.
      const mark = !scrolled && holder.querySelector("mark");
      if (mark && drawer === d) { scrolled = true; mark.scrollIntoView({ block: "start" }); }
    }).catch(() => {
      holder.replaceChildren(h("p", { class: "rule-note" }, "Couldn't load the text. ", href ? h("a", { href }, "Open the document") : null));
    });
  });
}
